import React, { useState } from 'react';
import { aiAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Linkedin, Twitter, Globe, Sparkles, Copy, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';

const ContentRepurposer = () => {
  const [content, setContent] = useState('');
  const [platform, setPlatform] = useState('linkedin');
  const [generatedContent, setGeneratedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const platforms = [
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: <Linkedin className="w-5 h-5" />,
      description: 'Professional post for your network',
      color: 'bg-blue-600',
    },
    {
      id: 'twitter',
      name: 'Twitter/X',
      icon: <Twitter className="w-5 h-5" />,
      description: 'Engaging thread for social media',
      color: 'bg-slate-900',
    },
    {
      id: 'portfolio',
      name: 'Portfolio',
      icon: <Globe className="w-5 h-5" />,
      description: 'Compelling copy for your website',
      color: 'bg-indigo-600',
    },
  ];

  const handleGenerate = async () => {
    if (!content.trim()) {
      toast.error('Please enter some content to repurpose');
      return;
    }

    setLoading(true);
    setGeneratedContent('');

    try {
      const response = await aiAPI.repurposeContent(content, platform);
      setGeneratedContent(response.data.content);
      toast.success('Content generated!');
    } catch (error) {
      toast.error('Failed to generate content');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedContent);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const samplePrompts = [
    "I'm a software engineer with 5 years of experience in React and Node.js. I recently led a project that reduced page load times by 40%.",
    "Just completed my AWS certification! Here's what I learned about cloud architecture and why it matters.",
    "Looking for new opportunities as a Product Manager. I specialize in B2B SaaS and have helped launch 3 successful products.",
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="repurpose-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Content Repurposer
          </h1>
          <p className="text-slate-600 mt-1">
            Transform your professional content into engaging social media posts
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Your Content
                </CardTitle>
                <CardDescription>
                  Enter your resume summary, achievements, or any professional content
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Paste your resume summary, work experience, achievements, or any content you want to repurpose..."
                  rows={8}
                  className="resize-none"
                  data-testid="content-input"
                />
                <div>
                  <p className="text-sm text-slate-500 mb-2">Or try a sample:</p>
                  <div className="space-y-2">
                    {samplePrompts.map((prompt, index) => (
                      <button
                        key={index}
                        onClick={() => setContent(prompt)}
                        className="block text-left text-sm text-indigo-600 hover:text-indigo-700 hover:underline"
                        data-testid={`sample-prompt-${index}`}
                      >
                        "{prompt.substring(0, 60)}..."
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Target Platform
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs value={platform} onValueChange={setPlatform}>
                  <TabsList className="grid grid-cols-3 w-full">
                    {platforms.map((p) => (
                      <TabsTrigger
                        key={p.id}
                        value={p.id}
                        className="flex items-center gap-2"
                        data-testid={`platform-${p.id}`}
                      >
                        {p.icon}
                        <span className="hidden sm:inline">{p.name}</span>
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {platforms.map((p) => (
                    <TabsContent key={p.id} value={p.id} className="mt-4">
                      <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                        <div className={`w-10 h-10 rounded-lg ${p.color} text-white flex items-center justify-center`}>
                          {p.icon}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{p.name}</p>
                          <p className="text-sm text-slate-600">{p.description}</p>
                        </div>
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>

            <Button
              onClick={handleGenerate}
              disabled={loading || !content.trim()}
              className="w-full bg-indigo-600 hover:bg-indigo-700 h-12"
              data-testid="generate-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Content
                </>
              )}
            </Button>
          </div>

          {/* Output Section */}
          <div>
            <Card className="h-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Generated Content
                  </CardTitle>
                  <CardDescription>
                    {generatedContent
                      ? `Ready for ${platforms.find((p) => p.id === platform)?.name}`
                      : 'Your repurposed content will appear here'}
                  </CardDescription>
                </div>
                {generatedContent && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                    data-testid="copy-btn"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-2 text-green-500" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </>
                    )}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-20 text-slate-500">
                    <Loader2 className="w-8 h-8 animate-spin mb-4 text-indigo-600" />
                    <p>Generating your content...</p>
                  </div>
                ) : generatedContent ? (
                  <div
                    className="p-4 bg-slate-50 rounded-lg whitespace-pre-wrap text-slate-700 leading-relaxed min-h-[300px]"
                    data-testid="generated-content"
                  >
                    {generatedContent}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-20 text-slate-500">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                      <Sparkles className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-center">
                      Enter your content and click "Generate Content" to see the magic happen
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ContentRepurposer;
